"use client";

import { c as _c } from "react/compiler-runtime";
import { getStripe } from "@/utils/stripe/client";
import { checkoutWithStripe } from "@/utils/stripe/server";
import { getErrorRedirect } from "@/utils/helpers";
import { useRouter, usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { getTrackingPricing } from "@/utils/tracking/pricing";
import { getCheckoutTrackingContext } from "@/utils/tracking/checkout";
import { sendMetaInitiateCheckoutEvent } from "@/actions/metaConversion";
import { sendTikTokInitiateCheckoutEvent } from "@/actions/tiktokConversion";
import { usePostHog } from "posthog-js/react";
import type { PublicOffer } from "@/lib/offers";
interface BuyEventButtonProps {
  className?: string;
  variant: "dark" | "light" | "lightest" | "christmas" | "white" | "konfirmation" | "barnedab" | "fodselsdag" | "barMitsva";
  packageType: "basic" | "premium";
  userEmail?: string;
  offerPlacement?: string;
  offer?: PublicOffer;
}
export default function BuyEventButton(t0) {
  const $ = _c(16);
  const {
    className,
    variant,
    packageType,
    userEmail,
    offerPlacement,
    offer
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const posthog = usePostHog();
  const [submitting, setSubmitting] = useState(false);
  const priceId = packageType === "basic" ? process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_BASIC : process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM;
  let t1;
  if ($[0] !== locale || $[1] !== offer || $[2] !== offerPlacement || $[3] !== packageType || $[4] !== pathname || $[5] !== posthog || $[6] !== priceId || $[7] !== router || $[8] !== userEmail) {
    t1 = async t2 => {
      const price = t2 === undefined ? {
        id: priceId
      } : t2;
      setSubmitting(true);
      const {
        checkoutFlow,
        checkoutPlace,
        checkoutLocale
      } = getCheckoutTrackingContext(pathname, locale);
      posthog.capture("clicked_buy_event", {
        package_type: packageType,
        packageType,
        platform: "web",
        checkout_flow: checkoutFlow,
        checkout_place: checkoutPlace,
        checkout_locale: checkoutLocale,
        is_upgrade: false,
        offer_placement: offerPlacement,
        offer_campaign_code: offer?.code
      });
      const checkoutLocaleValue = checkoutLocale;
      const fallbackPricing = getTrackingPricing(locale, packageType);
      const pricing = offer?.priceMinor === null || offer?.priceMinor === undefined ? fallbackPricing : {
        value: offer.priceMinor / 100,
        currency: offer.currency
      };
      const eventId = `ic_${Date.now()}_${packageType}`;
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "InitiateCheckout", {
          value: pricing.value,
          currency: pricing.currency
        }, {
          eventID: eventId
        });
      }
      if (typeof window !== "undefined" && window.ttq) {
        window.ttq.track("InitiateCheckout", {
          value: pricing.value,
          currency: pricing.currency,
          contents: [{
            content_id: "guestgallery_subscription",
            content_type: "product",
            quantity: 1,
            price: pricing.value
          }],
          content_type: "product"
        });
      }
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "begin_checkout", {
          value: pricing.value,
          currency: pricing.currency,
          items: [{
            item_id: "guestgallery_subscription",
            item_name: `Guest.Gallery ${packageType}`,
            quantity: 1,
            price: pricing.value
          }]
        });
      }
      if (userEmail) {
        sendMetaInitiateCheckoutEvent(userEmail, checkoutLocaleValue, packageType, eventId);
        sendTikTokInitiateCheckoutEvent(userEmail, checkoutLocaleValue, packageType, eventId);
      }
      const {
        errorRedirect,
        sessionId
      } = await checkoutWithStripe(price, "/thank-you", checkoutLocaleValue, checkoutPlace, packageType, offerPlacement);
      if (errorRedirect) {
        posthog.capture("checkout_redirect_failed", {
          package_type: packageType,
          checkout_flow: checkoutFlow,
          checkout_place: checkoutPlace,
          checkout_locale: checkoutLocaleValue,
          failure_stage: "session_creation",
          failure_reason: "error_redirect",
          platform: "web",
          offer_placement: offerPlacement,
          offer_campaign_code: offer?.code
        });
        return router.push(errorRedirect);
      }
      if (!sessionId) {
        posthog.capture("checkout_redirect_failed", {
          package_type: packageType,
          checkout_flow: checkoutFlow,
          checkout_place: checkoutPlace,
          checkout_locale: checkoutLocaleValue,
          failure_stage: "session_creation",
          failure_reason: "missing_session_id",
          platform: "web",
          offer_placement: offerPlacement
        });
        return router.push(getErrorRedirect("/home", "An unknown error occurred.", "Please try again later or contact a system administrator."));
      }
      const stripe = await getStripe();
      if (!stripe) {
        posthog.capture("checkout_redirect_failed", {
          package_type: packageType,
          checkout_flow: checkoutFlow,
          checkout_place: checkoutPlace,
          checkout_locale: checkoutLocaleValue,
          failure_stage: "stripe_initialization",
          failure_reason: "stripe_unavailable",
          stripe_checkout_session_id: sessionId,
          platform: "web",
          offer_placement: offerPlacement
        });
        setSubmitting(false);
        return;
      }
      const redirectResult = await stripe.redirectToCheckout({
        sessionId
      });
      if (redirectResult?.error) {
        posthog.capture("checkout_redirect_failed", {
          package_type: packageType,
          checkout_flow: checkoutFlow,
          checkout_place: checkoutPlace,
          checkout_locale: checkoutLocaleValue,
          failure_stage: "stripe_redirect",
          failure_reason: redirectResult.error.type || "stripe_error",
          stripe_checkout_session_id: sessionId,
          platform: "web",
          offer_placement: offerPlacement
        });
      }
      setSubmitting(false);
    };
    $[0] = locale;
    $[1] = offer;
    $[2] = offerPlacement;
    $[3] = packageType;
    $[4] = pathname;
    $[5] = posthog;
    $[6] = priceId;
    $[7] = router;
    $[8] = userEmail;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const handleStripeCheckout = t1;
  const t = useTranslations("Home.BuyEventCard");
  let t2;
  if ($[10] !== className || $[11] !== handleStripeCheckout || $[12] !== submitting || $[13] !== t || $[14] !== variant) {
    t2 = <>{!submitting ? <Button className={className} variant={variant} onClick={() => {
        handleStripeCheckout();
      }}><span className="flex items-center"><Plus className="mr-2 h-4 w-4" />{t("buyEvent")}</span></Button> : <Button className={className} disabled={true} variant={variant}><span className="flex items-center"><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("buttonLoading")}</span></Button>}</>;
    $[10] = className;
    $[11] = handleStripeCheckout;
    $[12] = submitting;
    $[13] = t;
    $[14] = variant;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}